<!--
  Asking a connected agent about a whole subject without a conversation (a
  branch's history): its page's Ask agent opens it. It is the review
  thread's agent panel (RelayThread) about the subject: a subject task
  (logic/prAsk.ts `subjectTask`), offered to the agents whose grant on it
  covers `metadata`, under the subject's own topic, so the answers and a
  reply box stay with it while it is open.
-->
<script lang="ts">
  import RelayThread, { type RelayAbout, type RelayPage } from './RelayThread.svelte';
  import { QUESTION_NEEDS, subjectTopic } from '../api/relayTask';
  import { subjectTask } from '../logic/prAsk';

  interface Props {
    page: RelayPage;
    /** What the subject is called in the heading, `the history of feat/x`. */
    what: string;
    onclose: () => void;
  }
  let { page, what, onclose }: Props = $props();
  const uid = $props.id();

  const about: RelayAbout = $derived({
    topic: subjectTopic(page.subject),
    task: (instruction: string) => {
      if (!page.head) throw new Error(`This ${page.noun} has not loaded yet. Try again in a moment.`);
      return subjectTask(page.subject, page.head, instruction);
    },
    needs: QUESTION_NEEDS,
    carries: `what the ${page.noun} is and its head`,
    canDraft: false,
    draft: async () => false,
    draftTitle: '',
    noun: 'question',
    asksHere: true,
  });
</script>

<aside class="subject-ask" aria-labelledby="{uid}-h">
  <div class="subject-ask-head">
    <h3 id="{uid}-h">Ask an agent <span class="muted">about {what}</span></h3>
    <button class="btn small" aria-label="Close" title="Close" onclick={onclose}>×</button>
  </div>
  <RelayThread {page} {about} />
</aside>

<style>
  .subject-ask {
    position: fixed;
    right: 16px;
    bottom: 72px;
    z-index: 5;
    width: min(460px, calc(100vw - 32px));
    max-height: 60vh;
    overflow: auto;
    padding: 12px 14px;
    border: 1px solid var(--line);
    border-radius: 10px;
    background: var(--surface);
    box-shadow: var(--shadow);
  }
  .subject-ask-head {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    justify-content: space-between;
  }
  .subject-ask-head h3 {
    margin: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
</style>
